import { useQuery, useReactiveVar } from "@apollo/client";
import { GET_DELIVERY_FEE, GET_SALES_TAX, GET_CONVENIENCE_FEE } from "../../../../Graphql/queries";
import { useEffect, useMemo, useState } from "react";
import { getPercentValue } from "@utils/order-format.utils";
import { foodAndBeverageTotal } from "../../../../Modules/Common/commonUtils";
import { authVar } from "../../../../ReactiveVariables";
import { useNavigate } from "react-router";
import { validateCartError, validateCartWarnings } from "./cart-view.utils";
import { ORDER_STATUS, ORDER_TIP_TYPE } from "@constants/order.constants";
import { useOrderCalculation } from "@utils/hooks/use-order-calculation";
import { IOrderPayload } from "@services/redux/order";
import { ROUTES } from "@constants/routes.constants";
import { useGroupOrder } from "@services/group-order/group-order.context";

interface ICartViewStateProps {
  caterer: {
    zip: string;
    city: string;
    state: string;
    id: string;
    minOrderAmount: number;
    isTipMandatory: boolean;
    isMandatoryTipPercentage: boolean;
    mandatoryTipValue: number;
  };
  customerId: string;
  order: IOrderPayload;
  tableware: {
    isCharged: boolean;
    cost: number;
  };
  user: Object;
  headCount: number;
  isAvailable: "date" | "address" | true;
  onUpdateOrder(params: Partial<IOrderPayload>): void;
  onUpdateTotalCost(total: number): void;
}

export function useCartViewState({
  caterer,
  order,
  user,
  customerId,
  tableware,
  headCount,
  isAvailable,
  onUpdateOrder,
  onUpdateTotalCost,
}: ICartViewStateProps) {
  const auth = useReactiveVar(authVar);
  const navigate = useNavigate();
  const groupOrderCtx = useGroupOrder();

  const [cartError, setCartError] = useState(null);
  const [cartWarnings, setCartWarnings] = useState([]);
  // MAIN-861: the office's "Finish order" dialog.
  const [finishOpen, setFinishOpen] = useState(false);

  const cartCost = useMemo(() => {
    return foodAndBeverageTotal(order.cartDetails);
  }, [order.cartDetails, order.cartDetails.length]);

  const cartItemsQuantity = useMemo(() => {
    return order.cartDetails.reduce((sum, item) => sum + item.quantity, 0);
  }, [order.cartDetails, order.cartDetails.length]);

  const mandatoryTip = useMemo(() => {
    return caterer.isTipMandatory
      ? caterer.isMandatoryTipPercentage
        ? getPercentValue(cartCost * caterer.mandatoryTipValue)
        : caterer.mandatoryTipValue
      : 0;
  }, [
    caterer.isTipMandatory,
    cartCost,
    caterer.isMandatoryTipPercentage,
    caterer.mandatoryTipValue,
  ]);

  const { data: salesTaxData } = useQuery(GET_SALES_TAX, {
    variables: {
      zip: caterer.zip,
      city: caterer.city,
      catererStateShort: caterer.state,
      customerId: customerId,
    },
  });

  const { data: deliveryFeeData } = useQuery(GET_DELIVERY_FEE, {
    variables: {
      catererId: caterer.id,
      totalCost: cartCost,
      orderType: order.deliveryMode,
      zipCode: order.zip,
    },
    onCompleted(data) {
      onUpdateOrder({
        deliveryFee: data.getDeliveryFee.deliveryFee,
      });
    },
  });

  const { data: convenienceFeeData } = useQuery(GET_CONVENIENCE_FEE, {
    variables: {
      totalCost: cartCost,
      customerId: customerId || undefined,
    },
    onCompleted(data) {
      onUpdateOrder({
        convenienceFee: data.getConvenienceFee.convenienceFee,
      });
    },
  });

  const tablewareCost = useMemo(() => {
    if (order.tablewareDeclined || !tableware.isCharged) {
      return 0;
    }
    return headCount * tableware.cost;
  }, [tableware, order.tablewareDeclined, headCount]);

  const { taxCost, totalCost } = useOrderCalculation({
    cartCost,
    tableware: tablewareCost,
    salesTaxPercent: salesTaxData ? salesTaxData.getSalesTax.salesTax : 0,
    deliveryFee: deliveryFeeData ? deliveryFeeData.getDeliveryFee.deliveryFee : 0,
    driverTip: caterer.isTipMandatory ? mandatoryTip : order.driverTip,
    convenienceFee: convenienceFeeData ? convenienceFeeData.getConvenienceFee.feeTotal : 0,
    discount: 0,
  });

  useEffect(() => {
    onUpdateTotalCost(totalCost);
  }, [totalCost]);

  useEffect(() => {
    if (!order.tipPercentage) {
      return;
    }

    onUpdateOrder({
      driverTip: getPercentValue(cartCost * order.tipPercentage),
    });
  }, [cartCost]);

  function onChangePercentageTip(value: number | null) {
    const newTip = value ? getPercentValue(cartCost * value) : 0;

    onUpdateOrder({
      driverTip: newTip,
      tipPercentage: value,
      driverTipType: ORDER_TIP_TYPE.percentage,
    });
  }

  function onChangeCustomTip(value: number) {
    const newTip = value || 0;

    onUpdateOrder({
      driverTip: newTip,
      tipPercentage: 0,
      driverTipType: ORDER_TIP_TYPE.custom,
    });
  }

  function onAddTableware() {
    onUpdateOrder({
      tablewareTotal: headCount * tableware.cost,
      tablewareDeclined: false,
    });
  }

  function onRemoveTableware() {
    onUpdateOrder({
      tablewareTotal: 0,
      tablewareDeclined: true,
    });
  }

  async function onDeleteCartItem(id, index) {
    const newCartData = [...order.cartDetails];
    newCartData.splice(index, 1);
    onUpdateOrder({
      cartDetails: newCartData,
    });
  }

  function onCloseCartError() {
    setCartError(null);
  }

  function onCloseCartWarnings() {
    setCartWarnings([]);
  }

  function onNextWarning() {
    const newWarnings = cartWarnings.length ? cartWarnings.slice(1) : cartWarnings;
    setCartWarnings(newWarnings);
    if (!newWarnings.length) {
      onNextStep();
    }
  }

  function onSubmit() {
    const isOffice = groupOrderCtx?.mode === "office";
    const error = validateCartError({
      address: order.street,
      zip: order.zip,
      headCount,
      isAvailable: isAvailable,
      minOrderAmount: caterer.minOrderAmount,
      totalCost,
      date: order.deliveryDate ? new Date(order.deliveryDate) : null,
      user,
      // MAIN-861: the office isn't the account holder; skip the profile checks.
      auth: isOffice ? null : auth,
    });

    if (error) {
      return setCartError(error);
    }

    const warnings = [...validateCartWarnings({
      headCount,
      cartItemsQuantity: cartItemsQuantity,
      isTableWareCharged: tableware.isCharged,
      tip: order.driverTip,
      deliveryMode: order.deliveryMode,
      isTipMandatory: caterer.isTipMandatory,
    })];
    // MAIN-861: the rep can check out before the office has finished.
    if (groupOrderCtx?.mode === "rep" && groupOrderCtx.groupOrder?.status === "Open") {
      warnings.push({
        title: "The office hasn't finished yet",
        description: "They may still be adding items. Check out anyway?",
      });
    }

    if (warnings.length) {
      return setCartWarnings(warnings);
    }

    onNextStep();
  }

  async function onNextStep() {
    // MAIN-861: the office doesn't check out; it sends the cart to the rep.
    if (groupOrderCtx?.mode === "office") {
      setFinishOpen(true);
      return;
    }
    // MAIN-861: the rep's cart is saved and locked before checkout. If that fails, or
    // the cart was updated meanwhile, the provider already told the user; stay on this page.
    if (groupOrderCtx?.mode === "rep") {
      try {
        await groupOrderCtx.startCheckout();
      } catch (e) {
        return;
      }
    }
    if (auth.auth) {
      navigate(
        `${ROUTES.checkout}/${
          !order.orderStatus || order.orderStatus === ORDER_STATUS.draft || !order._id
            ? "new"
            : order._id
        }`,
      );
    } else if (!auth.auth) {
      navigate(`${ROUTES.login}?redirect=${ROUTES.checkout}/new`);
    }
  }

  return {
    alertModal: {
      onClose: cartError ? onCloseCartError : onCloseCartWarnings,
      onContinue: onNextWarning,
      type: cartError ? "error" : cartWarnings.length ? "warning" : "hidden",
      title: cartError ? cartError.title : cartWarnings.length ? cartWarnings[0].title : "",
      description: cartError
        ? cartError.description
        : cartWarnings.length
        ? cartWarnings[0].description
        : "",
    },
    cart: {
      cartItemsQuantity,
      onDeleteCartItem,
    },
    tip: {
      value: order.driverTip,
      percentageTip: order.tipPercentage,
      mandatoryTip,
      onChangeCustomTip,
      onChangePercentageTip,
    },
    salesTax: {
      percentage: salesTaxData ? salesTaxData.getSalesTax.salesTax : 0,
      value: taxCost,
    },
    tableware: {
      value: tablewareCost,
      isTablewareRemoved: order.tablewareDeclined,
      onAddTableware,
      onRemoveTableware,
    },
    convenienceFee: {
      value: convenienceFeeData ? convenienceFeeData.getConvenienceFee.feeTotal : 0,
      description: convenienceFeeData ? convenienceFeeData.getConvenienceFee.description : "",
    },
    cost: {
      totalCost,
      cartCost,
      deliveryFee: deliveryFeeData ? deliveryFeeData.getDeliveryFee.deliveryFee : 0,
    },
    onSubmit,
    groupOrderCtx,
    finish: { open: finishOpen, onClose: () => setFinishOpen(false) },
  };
}
